import type { Ref } from 'vue'
import { computed, ref } from 'vue'

import { defineStore } from 'pinia'

import { asyncComputed } from '@vueuse/core'
import { format } from 'date-fns-tz'
import { useI18n } from 'vue-i18n'

import { useNotifications } from '@tsyl/zapan2'

import { getIncident } from '@/services/api/incidents'
import { getCalculationTypes } from '@/services/api/weather'
import type { WeatherTypeData, WeatherCategoryFiltered, CalculationCategoryName, CalculationKindName } from '@/types/api/Calculations'
import type { CategoryData, Incident, Measurements, ValueBufferType } from '@/types/Incident'
import type { User } from '@/types/User'
import type { WeatherCalculation } from '@/types/WeatherMeteo'

export default defineStore('incident', () => {
  const incident: Ref<Incident | undefined> = ref<Incident>()
  const loadingIncident = ref(false)
  const WEATHER_KIND_ID = 'c3abf83a-01a8-4cf2-98f3-788c6644616e'
  const { addNotification } = useNotifications()
  const statusCode = ref<number>()
  const { t } = useI18n()

  const bufferTypes: string[] = ['1', '5', '10']

  function setIncident(newIncident: Incident) {
    incident.value = newIncident
  }

  function measurementsOfCategory(categoryName: string): Measurements[] | undefined {
    if (incident.value === undefined) return undefined

    if (incident.value?.measurements) {
      return incident.value.measurements
        .filter((x) => x.measurementType.incidentMeasurementCategory?.name === categoryName)
        .sort((a, b) => (a.measurementType.displayOrder ?? 0) - (b.measurementType.displayOrder ?? 0))
    }
    return []
  }

  const population = computed(() => measurementsOfCategory('population'))
  const buildings = computed(() => measurementsOfCategory('buildings'))

  async function fetchIncident(id: string) {
    loadingIncident.value = true
    const res = await getIncident(id)
    if (isError(res)) {
      // save code
      statusCode.value = res
      // notification
      switch (res) {
        case 404:
          addNotification({
            type: 'error',
            title: t('messages.error.incidentNotFound'),
            time: 10000
          })
          break
        case 403:
          addNotification({
            type: 'warning',
            title: t('messages.error.incidentNoAccess'),
            time: 10000
          })
          break
        case 400:
          addNotification({
            type: 'warning',
            title: t('messages.error.generic'),
            time: 10000
          })
          break
      }
    } else {
      setIncident(res)
      statusCode.value = undefined
    }
    loadingIncident.value = false
  }

  function isError(incident: Incident | number): incident is number {
    return typeof incident === 'number'
  }

  // ######################################### WEATHER FUNCTIONS ########################################
  const weatherTypes = asyncComputed(
    async () => {
      return await getCalculationTypes()
    },
    undefined,
    { onError: console.error }
  )

  const weatherCalculations = computed((): WeatherCalculation[] | undefined => {
    // To control the existence or not of Weather Data
    if (incident.value === undefined) return undefined
    if (weatherTypes.value === undefined) return undefined

    // If there is an incident loaded
    const data = [] as WeatherCalculation[]

    if (incident.value?.calculations && incident.value?.calculations.length > 0) {
      let calculationCategories: CategoryData[] = []
      for (let j = 0; j < bufferTypes.length; j++) {
        calculationCategories = []
        for (let i = 0; i < weatherTypes.value.length; i++) {
          const calculationData: CategoryData | undefined = calculationsWeather(weatherTypes.value[i], bufferTypes[j])
          if (calculationData !== undefined) calculationCategories.push(calculationData)
        }
        if (calculationCategories.length > 0) {
          data.push({
            buffer: bufferTypes[j],
            data: calculationCategories
          })
        }
      }
    }
    return data
  })

  function calculationsWeather(weatherCategory: WeatherTypeData, buffer: string): CategoryData | undefined {
    const categories = incident.value?.calculations.filter((calc) => calc.calculationCategory?.calculationKind.id === WEATHER_KIND_ID)

    if (categories === undefined) return undefined

    const categoryArray = categories?.filter((cat) => cat.calculationCategory?.id === weatherCategory.id)

    let bufferCalculations: WeatherCategoryFiltered[] = []
    switch (buffer) {
      case '1':
        bufferCalculations = categoryArray?.map((category) => category.valueBuffer1) as WeatherCategoryFiltered[]
        break
      case '5':
        bufferCalculations = categoryArray?.map((category) => category.valueBuffer5) as WeatherCategoryFiltered[]
        break
      case '10':
        bufferCalculations = categoryArray?.map((category) => category.valueBuffer10) as WeatherCategoryFiltered[]
        break
    }

    // Drop rows the backend didn't bucket (Count === 0 → no calculationType).
    // Without this, the chart tries to plot a category with no danger band.
    bufferCalculations = bufferCalculations.filter((x) => x?.calculationType !== undefined)
    if (bufferCalculations.length === 0) return undefined

    return parseWeatherCalculationData(
      bufferCalculations.sort((a, b) => new Date(a.forecastTime).getTime() - new Date(b.forecastTime).getTime()),
      weatherCategory
    )
  }

  function parseWeatherCalculationData(calculations: WeatherCategoryFiltered[], weatherCategory: WeatherTypeData): CategoryData {
    return {
      id: weatherCategory.id,
      name: '',
      initials: '',
      name_key: weatherCategory.name,
      initials_key: weatherCategory.initials,
      icon: weatherCategory.icon,
      variableData: {
        id: weatherCategory.id,
        times: calculations.map((x) => format(new Date(x.forecastTime), 'yyyy-MM-dd')),
        colors: weatherCategory.colors,
        values: calculations.map((x) => x.calculationType?.displayOrder ?? -1)
      }
    }
  }

  // ######################################### AUTH FUNCTIONS ###########################################
  function isFromUserOrganization(user: User, incident: Incident) {
    return incident.organizationId === user.organization
  }

  // ######################################### WEATHER FUNCTIONS ########################################

  /**
   * Retrieve incident calculations based on calculation kind, calculation category and selected buffer
   * @param categoryName Calculation category (Landcover, Elevation, slope...)
   * @param kindName Calculation kind (Fuels, Weather, Topography)
   * @param buffer Selected buffer (1, 5, 10)
   */
  function getIncidentCalculations(
    kindName: CalculationKindName,
    categoryName: CalculationCategoryName,
    buffer: string
  ): ValueBufferType | undefined {
    const categories = incident.value?.calculations.filter((calc) => calc.calculationCategory?.calculationKind.name === kindName)

    const category = categories?.find((cat) => cat.calculationCategory?.name === categoryName)

    switch (buffer) {
      case '1':
        return category?.valueBuffer1
      case '5':
        return category?.valueBuffer5
      case '10':
        return category?.valueBuffer10
      default:
        console.error(`Selected buffer: ${buffer} is not available`)
    }
  }

  return {
    incident,
    population,
    buildings,
    weatherCalculations,
    getIncidentCalculations,
    loadingIncident,
    fetchIncident,
    statusCode,
    isFromUserOrganization
  }
})
